import * as React from 'react';

import styled from 'styled-components';

/* eslint-disable */
import { Typography } from 'components/DesignSystemV2/Typography';
import { LazyImage } from 'components/LazyImage';
import { Panel, PanelBody, PanelHeader } from 'components/panel';
import { withMarketingSection } from 'components/withMarketingSection';
import { withHomePageSection } from 'components/withHomePageSection';
import { colorsV2 } from 'style/colors-v2';
import useMediaQuery from 'utils/react-responsive';

import { deviceBreakpoints } from '../../../utils/hooks/useDeviceQuery.hook';
import { minsToHours } from '../Courses';
import { HeadingContainer } from '../HeadingContainer';
import { SectionSeparator } from '../SectionSeparator';
import { fromScreen } from 'utils/media-query/responsive.util';
import { useGCMSLevel } from './useGCMSLevel';

const PAGE_SIZE = 7;

export const SyllabusDetail = withHomePageSection(
  props => {
    const { syllabus, level } = props;
    const isGmatGre = level.classGroup.includes('GMAT/GRE');

    const [page, setPage] = React.useState(0);
    const { isTabletOrMobile } = useMediaQuery();
    const [isMounted, setMounted] = React.useState(false);

    React.useEffect(() => {
      setMounted(true);
    }, []);

    const sessions = React.useMemo(() =>
      syllabus.nodes
        .reduce((result, currentSession) => {
          const found = result.find(
            session => session.order === +currentSession.session
          );

          if (found) {
            found.items.push(currentSession);
          } else {
            result.push({
              title: currentSession.sessionTitle,
              order: +currentSession.session,
              items: [currentSession]
            });
          }

          return result;
        }, [])
        .sort((s1, s2) => s1.order - s2.order)
        .map(session => ({
          ...session,
          totalDurationInMinutes: session.items.reduce(
            (sum, item) => sum + +item.durationInMinutes,
            0
          )
        }))
    );

    const totalWeeks = syllabus.nodes[0].totalWeeks;
    const totalSessionsDisplay = syllabus.nodes[0].totalSessions;
    const totalCourseDurationInMinutes = sessions.reduce(
      (sum, session) => sum + session.totalDurationInMinutes,
      0
    );

    const remainItems = totalSessionsDisplay - (page + 1) * PAGE_SIZE;

    let nextSlice = sessions.length;

    nextSlice = (page + 1) * PAGE_SIZE;

    if (nextSlice > sessions.length) {
      nextSlice = sessions.length;
    }

    return (
      <Container id="syllabus-detail">
        <HeadingContainer>
          <Typography
            variant="bold/20-28"
            desktopVariant="bold/24-32"
            className="section-title"
            v3
            as="h2"
          >{`Nội dung chương trình học ${totalWeeks} tuần`}</Typography>
        </HeadingContainer>

        <Typography
          style={{ marginBottom: 16 }}
          variant="medium/14-20"
        >{`${totalSessionsDisplay} Buổi${
          isGmatGre ? '' : ` • ${syllabus.nodes.length} Bài học`
        } ${
          level?.levelTotalHour
            ? ` • ${level?.levelTotalHour}h total length`
            : ''
        }`}</Typography>

        <Sessions>
          {sessions.slice(0, nextSlice).map((session, idx) => (
            <Session key={session.title} expanded={idx === 0}>
              <SessionHeader>
                <HeaderLayout>
                  <Typography variant="semi-bold/14">
                    {session.title}
                  </Typography>
                  <Typography className="mins" variant="regular/14">
                    {session.totalDurationInMinutes
                      ? `${session.totalDurationInMinutes} mins`
                      : ''}
                  </Typography>
                </HeaderLayout>
              </SessionHeader>

              <PanelBody>
                {session.items.map(item => (
                  <SyllabusLayout
                    style={{ marginBottom: 16 }}
                    key={item.syllabus}
                  >
                    {item.iconHandle && item.iconHandle.length > 2 ? (
                      <LazyImage
                        imgProps={{
                          alt: 'syllabus'
                        }}
                        handle={item.iconHandle}
                      />
                    ) : (
                      <span />
                    )}
                    <Typography
                      variant="regular/14"
                      style={{ color: colorsV2.black80 }}
                    >
                      {item.syllabus}
                    </Typography>
                    {item.durationInMinutes && (
                      <Typography
                        variant="regular/14"
                        style={{ color: colorsV2.black80 }}
                      >
                        {`${
                          item.durationInMinutes.toString().length < 2
                            ? `0${item.durationInMinutes}`
                            : item.durationInMinutes
                        } mins`}
                      </Typography>
                    )}
                  </SyllabusLayout>
                ))}
              </PanelBody>
            </Session>
          ))}
        </Sessions>

        {remainItems > 0 ? (
          <Button
            className="btn-see-more"
            onClick={() => {
              setPage(page + 1);
            }}
          >
            <Typography variant="semi-bold/14-24">{`Xem thêm ${remainItems} buổi`}</Typography>
          </Button>
        ) : null}
      </Container>
    );
  },
  { selfish: true }
);

SyllabusDetail.propTypes = {};

const SessionHeader = styled(PanelHeader)`
  flex-flow: row-reverse;
  background: ${colorsV2.gray5};
  border-radius: 8px;

  &.expanded {
    border: none;
    border-radius: 6px 6px 0 0;
    border-bottom: 1px solid ${colorsV2.gray20};
    position: relative;
  }
`;

const Button = styled.button`
  cursor: pointer;
  outline: none;
  appearance: none;
  width: 100%;
  padding: 10px 0;
  border-radius: 8px;
  border: 1px solid ${colorsV2.primary100};
  transition: 300ms;
  background: ${colorsV2.white100};
  margin-top: 16px;

  * {
    text-align: center;
    color: ${colorsV2.primary100};
  }

  &:hover {
    border: 1px solid ${colorsV2.primary80};

    * {
      color: ${colorsV2.primary80};
    }
  }
`;

const Session = styled(Panel)`
  border-radius: 8px;
  background-color: ${colorsV2.black5};

  &.expanded {
    border: 1px solid ${colorsV2.gray20};
    background-color: ${colorsV2.white100};
  }
`;

const Sessions = styled.div`
  display: grid;
  gap: 16px;
`;

const Container = styled.div`
  ${fromScreen(776)} {
    .section-title {
      font-size: 24px;
      line-height: 32px;
    }
  }
`;

const SyllabusLayout = styled.div`
  display: grid;
  grid-template-columns: 20px 1fr max-content;
  gap: 18px 16px;
`;

const HeaderLayout = styled.div`
  margin-left: 8px;
  display: grid;
  gap: 12px;
  grid-template-columns: 1fr max-content;
  .mins {
    grid-column: 3;
  }
`;
